import React, { useState, useEffect } from 'react';
import { Plus, Image, Loader2, Hash } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../ui/form';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { useToast } from '../../ui/use-toast';
import { supabase } from '../../../lib/supabase';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const contestantSchema = z.object({
  name: z.string().min(1, 'Le nom est requis'),
  description: z.string().min(1, 'La description est requise'),
  unique_code: z.string().min(1, 'Le code unique est requis'),
  contest_id: z.string().min(1, 'Le concours est requis'),
  image: z
    .instanceof(FileList)
    .optional()
    .refine((files) => {
      if (files && files.length > 0) {
        return files[0].size <= MAX_FILE_SIZE;
      }
      return true;
    }, 'La taille maximale du fichier est de 5MB')
    .refine((files) => {
      if (files && files.length > 0) {
        return ACCEPTED_IMAGE_TYPES.includes(files[0].type);
      }
      return true;
    }, 'Format accepté : .jpg, .jpeg, .png, .webp'),
});

type ContestantFormValues = z.infer<typeof contestantSchema>;

interface Contest {
  id: string;
  name: string;
  code_unique?: string;
  end_date?: string;
  status?: string;
}

export const CreateContestantButton = () => {
  const [open, setOpen] = useState(false);
  const [contests, setContests] = useState<Contest[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm<ContestantFormValues>({
    resolver: zodResolver(contestantSchema),
    defaultValues: {
      name: '',
      description: '',
      unique_code: '',
      contest_id: '',
    },
  });

  useEffect(() => {
    const fetchContests = async () => {
      const { data, error } = await supabase
        .from('contests')
        .select('id, name, code_unique, end_date, status')
        .order('name');

      if (error) {
        toast({
          variant: 'destructive',
          title: 'Erreur',
          description: 'Impossible de charger les concours',
        });
      } else {
        const now = new Date();
        const filtered = (data || []).filter((c: any) => {
          if (c.status && c.status.toLowerCase() === 'finished') return false;
          if (c.end_date && new Date(c.end_date) < now) return false;
          return true;
        });
        setContests(filtered);
      }
    };

    if (open) {
      fetchContests();
    }
  }, [open]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const uploadImage = async (file: File, contestantId: string): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${contestantId}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('candidates')
      .upload(filePath, file, {
        upsert: true,
      });

    if (uploadError) {
      throw uploadError;
    }

    const { data } = supabase.storage
      .from('candidates')
      .getPublicUrl(filePath);

    return data.publicUrl;
  };

  const generateUniqueCode = (contestCode: string, candidateName: string): string => {
    const normalizedName = candidateName
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Supprimer les accents
      .replace(/[^a-z0-9]/g, '-') // Remplacer les caractères non alphanumériques par des tirets
      .replace(/-+/g, '-') // Remplacer les séquences de tirets par un seul tiret
      .replace(/^-|-$/g, ''); // Supprimer les tirets au début et à la fin

    return `${contestCode}-${normalizedName}`;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    const contestId = form.getValues('contest_id');
    
    if (name && contestId) {
      const selectedContest = contests.find(c => c.id === contestId);
      if (selectedContest && selectedContest.code_unique) {
        const uniqueCode = generateUniqueCode(selectedContest.code_unique, name);
        form.setValue('unique_code', uniqueCode);
      }
    }
  };

  const handleContestChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const contestId = e.target.value;
    const name = form.getValues('name');
    
    if (contestId && name) {
      const selectedContest = contests.find(c => c.id === contestId);
      if (selectedContest && selectedContest.code_unique) {
        const uniqueCode = generateUniqueCode(selectedContest.code_unique, name);
        form.setValue('unique_code', uniqueCode);
      }
    }
  };

  const onSubmit = async (data: ContestantFormValues) => {
    try {
      setIsSubmitting(true);

      // Insert contestant
      const { data: newContestant, error: insertError } = await supabase
        .from('contestants')
        .insert({
          name: data.name,
          description: data.description,
          unique_code: data.unique_code,
          contest_id: data.contest_id,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Upload image if provided
      if (data.image && data.image.length > 0 && newContestant) {
        const imageUrl = await uploadImage(data.image[0], newContestant.id);
        
        // Update contestant with image URL
        const { error: updateError } = await supabase
          .from('contestants')
          .update({ photo_url: imageUrl })
          .eq('id', newContestant.id);

        if (updateError) throw updateError;
      }

      toast({
        title: 'Succès',
        description: 'Le candidat a été créé',
      });

      // Reset form and close dialog
      form.reset();
      setImagePreview(null);
      setOpen(false);

      // Trigger table refresh (you'll need to implement this)
      window.location.reload();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.message || 'Impossible de créer le candidat',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button
        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-white bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-500 hover:to-yellow-400 transition-all duration-200 shadow-lg shadow-yellow-500/20 hover:shadow-yellow-500/40 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:ring-offset-2 focus:ring-offset-gray-900"
        onClick={() => setOpen(true)}
      >
        <Plus className="h-5 w-5" />
        Nouveau Candidat
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-gray-900 border border-white/10 text-white max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl font-bold bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 text-transparent bg-clip-text">
              Créer un nouveau candidat
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto space-y-6 pr-6 -mr-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-200">Nom</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            handleNameChange(e);
                          }}
                          className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                        />
                      </FormControl>
                      <FormMessage className="text-red-400" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-200">Description</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50 min-h-[100px]"
                        />
                      </FormControl>
                      <FormMessage className="text-red-400" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="unique_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-200">Code unique</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Hash className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                          <Input
                            {...field}
                            className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50 font-mono"
                            readOnly
                          />
                        </div>
                      </FormControl>
                      <FormMessage className="text-red-400" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="contest_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-200">Concours</FormLabel>
                      <FormControl>
                        <select
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            handleContestChange(e);
                          }}
                          className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-transparent"
                        >
                          <option value="" className="bg-gray-900">Sélectionner un concours</option>
                          {contests.map((contest) => (
                            <option key={contest.id} value={contest.id} className="bg-gray-900">
                              {contest.name}
                            </option>
                          ))}
                        </select>
                      </FormControl>
                      <FormMessage className="text-red-400" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="image"
                  render={({ field: { onChange, value, ...field } }) => (
                    <FormItem>
                      <FormLabel className="text-gray-200">Photo</FormLabel>
                      <FormControl>
                        <div className="space-y-4">
                          {/* Zone de drag & drop */}
                          <div 
                            className="relative w-40 h-40 rounded-xl border-2 border-dashed border-yellow-400/30 bg-white/5 flex items-center justify-center cursor-pointer group hover:border-yellow-400 transition-all"
                            onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                            onDrop={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              const file = e.dataTransfer.files?.[0];
                              if (file && ACCEPTED_IMAGE_TYPES.includes(file.type) && file.size <= MAX_FILE_SIZE) {
                                const dt = new DataTransfer();
                                dt.items.add(file);
                                // Simule le changement sur l'input caché
                                const input = document.getElementById('image-upload') as HTMLInputElement;
                                if (input) input.files = dt.files;
                                onChange(dt.files);
                                handleImageChange({ target: { files: dt.files } } as any);
                              }
                            }}
                          >
                            {imagePreview ? (
                              <img
                                src={imagePreview}
                                alt="Preview"
                                className="w-full h-full object-cover rounded-xl"
                              />
                            ) : (
                              <div className="flex flex-col items-center justify-center text-gray-400">
                                <Image className="h-10 w-10 mb-2" />
                                <span>Glissez-déposez une image<br/>ou cliquez</span>
                              </div>
                            )}
                            {/* Bouton suppression */}
                            {imagePreview && (
                              <button
                                type="button"
                                onClick={() => {
                                  setImagePreview(null);
                                  onChange(null);
                                }}
                                className="absolute top-2 right-2 p-2 bg-red-500/80 hover:bg-red-500 rounded-xl text-white transition-colors opacity-80 hover:opacity-100"
                              >
                                Supprimer
                              </button>
                            )}
                            {/* Input caché pour compatibilité */}
                            <Input
                              type="file"
                              accept={ACCEPTED_IMAGE_TYPES.join(',')}
                              onChange={e => {
                                onChange(e.target.files);
                                handleImageChange(e);
                              }}
                              {...field}
                              className="hidden"
                              id="image-upload"
                            />
                          </div>
                        </div>
                      </FormControl>
                      <FormMessage className="text-red-400" />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter className="flex-shrink-0 pt-6 border-t border-white/10 mt-6">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-500 hover:to-yellow-400 text-white shadow-lg shadow-yellow-500/20 hover:shadow-yellow-500/40"
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Création...
                    </div>
                  ) : (
                    'Créer'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};
